import { RoleCodes } from '@auth';

/**
 * Utility functions for working with role codes in route guards and authorization checks.
 */

/**
 * Creates a negative role string (exclusion) from a RoleCodes enum value.
 * This is an internal helper function used by roleArray().
 *
 * @private
 */
function excludeRole(role: RoleCodes): string {
  return `-${role}`;
}

/**
 * Creates an array of roles with exclusions for route configuration.
 * This is the standard way to define role-based route access control.
 *
 * @param include - Array of roles to require (user must have at least one). Defaults to empty array.
 * @param exclude - Array of roles to exclude (user must NOT have any). Defaults to empty array.
 * @returns Combined array of positive and negative role strings
 *
 * @example
 * // Require Admin role
 * data: { roles: roleArray([RoleCodes.Admin]) }
 *
 * @example
 * // Only exclude AvailabilityOnly (allow all other users)
 * data: { roles: roleArray([], [RoleCodes.AvailabilityOnly]) }
 *
 * @example
 * // Require Admin AND exclude AvailabilityOnly
 * data: { roles: roleArray([RoleCodes.Admin], [RoleCodes.AvailabilityOnly]) }
 *
 * @example
 * // Multiple includes and excludes
 * data: { roles: roleArray([RoleCodes.Admin, RoleCodes.RSIStaff], [RoleCodes.AvailabilityOnly]) }
 *
 * @example
 * // No restrictions (allow all authenticated users)
 * // Omit data.roles entirely or use: data: { roles: roleArray() }
 */
export function roleArray(
  include: RoleCodes[] = [],
  exclude: RoleCodes[] = [],
): string[] {
  return [...include, ...exclude.map((role) => excludeRole(role))];
}
